import {
  FormControl,
  FormHelperText,
  FormLabel,
  Select,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { useForm } from "react-hook-form";

import { Button, SettingsPageContainer, useToast } from "../../../components";
import {
  AiInterviewerVideoMode,
  CurrentUserFragment,
  useUpdateOrganizationAiInterviewerVideoSettingsMutation,
} from "../../graphql";

interface AiInterviewerSettingsProps {
  currentUser: CurrentUserFragment;
}

interface FormData {
  aiInterviewerVideoMode: "DISABLED" | "OPTIONAL" | "REQUIRED";
}

const AiInterviewerSettings: React.FC<AiInterviewerSettingsProps> = ({
  currentUser,
}) => {
  const toast = useToast();
  const orgVideoMode = currentUser.organization.aiInterviewerVideoMode as
    | "DISABLED"
    | "OPTIONAL"
    | "REQUIRED";

  const { register, handleSubmit } = useForm<FormData>({
    defaultValues: {
      aiInterviewerVideoMode: orgVideoMode,
    },
  });

  const [updateOrganization, { loading }] =
    useUpdateOrganizationAiInterviewerVideoSettingsMutation({
      onCompleted(data) {
        if (data.updateOrganizationAiInterviewerVideoSettings?.currentUser) {
          toast({
            title: "Success",
            description: "AI Interviewer video settings saved",
            status: "success",
          });
        }
      },
      onError(error) {
        toast({
          title: "Error",
          description: `Failed to save settings: ${error.message}`,
          status: "error",
        });
      },
    });

  const onSubmit = (values: FormData): void => {
    updateOrganization({
      variables: {
        aiInterviewerVideoMode:
          values.aiInterviewerVideoMode as unknown as AiInterviewerVideoMode,
      } as any,
    });
  };

  return (
    <SettingsPageContainer
      heading="Screen"
      subHeading="Configure AI Interviewer video settings for your organization."
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={6} align="stretch">
          <FormControl id="aiInterviewerVideoMode">
            <FormLabel>Default AI interviewer video setting</FormLabel>
            <Select {...register("aiInterviewerVideoMode")}>
              <option value="DISABLED">Disabled</option>
              <option value="OPTIONAL">Optional</option>
              <option value="REQUIRED">Required</option>
            </Select>
            <FormHelperText>
              Sets the default video setting for new AI interviewers.
            </FormHelperText>
          </FormControl>
          <Button type="submit" isLoading={loading}>
            Save
          </Button>
        </VStack>
      </form>
    </SettingsPageContainer>
  );
};

export default AiInterviewerSettings;
